export function tsToMs(ts) {
  if (!ts) return null;
  return ts < 10_000_000_000 ? ts * 1000 : ts;
}

export function coerceNotesArray(rawNotes = []) {
  if (typeof rawNotes === 'string') {
    try {
      const parsed = JSON.parse(rawNotes);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [rawNotes];
    }
  }
  return Array.isArray(rawNotes) ? rawNotes : [];
}

export function noteToText(note) {
  if (typeof note === 'string') return note.replace(/^,/, '').trim();
  if (!note || typeof note !== 'object') return '';
  return String(note.text || '').trim();
}

export function getPhotoCount(notes) {
  return notes.filter(note => note && typeof note === 'object' && note.photoUrl).length;
}

export function getCategorySummary(truck) {
  return (truck?.repairCategories || [])
    .map(c => [c?.category, c?.subcategory].filter(Boolean).join(' - '))
    .filter(Boolean);
}

export function getTechSummary(truck) {
  if (truck?.techNumbers?.length) return truck.techNumbers.filter(Boolean).map(String);
  return truck?.techNumber ? [String(truck.techNumber)] : [];
}

export function getCycleMs(truck) {
  const start = tsToMs(truck?.startTime);
  const end = tsToMs(truck?.completionTime || truck?.lastStatusChange);
  if (!start || !end || end < start) return null;
  return end - start;
}

export function getServiceRecordSummary(truck) {
  const saved = parseServiceSummary(truck?.serviceSummary);
  if (saved) return saved;

  const notes = coerceNotesArray(truck?.notes);
  const cycleMs = getCycleMs(truck);
  const cycleSeconds = cycleMs == null ? null : Math.round(cycleMs / 1000);

  return {
    truckId: truck?.id || null,
    repairOrder: truck?.repairOrder || '',
    status: truck?.status || '',
    completedDate: truck?.completionTime || truck?.lastStatusChange || null,
    totalCycleSeconds: cycleSeconds,
    noteCount: notes.length,
    photoCount: getPhotoCount(notes),
    primaryTech: getTechSummary(truck)[0] || null,
    techSummary: getTechSummary(truck),
    categorySummary: getCategorySummary(truck),
    photoKeys: notes
      .filter(note => note && typeof note === 'object' && note.photoUrl)
      .map(note => note.photoUrl),
  };
}

export function parseServiceSummary(serviceSummary) {
  if (!serviceSummary) return null;
  if (typeof serviceSummary === 'object') return serviceSummary;
  try {
    const parsed = JSON.parse(serviceSummary);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function createServiceSummaryJson(truck) {
  const withoutSavedSummary = { ...truck, serviceSummary: null };
  return JSON.stringify(getServiceRecordSummary(withoutSavedSummary));
}

export function buildServiceRecordJson(truck) {
  const notes = coerceNotesArray(truck?.notes);
  return {
    summary: getServiceRecordSummary(truck),
    job: {
      id: truck?.id,
      repairOrder: truck?.repairOrder,
      companyName: truck?.companyName,
      truckNumber: truck?.truckNumber,
      status: truck?.status,
      startTime: truck?.startTime,
      completionTime: truck?.completionTime,
      lastStatusChange: truck?.lastStatusChange,
      year: truck?.year,
      make: truck?.make,
      model: truck?.model,
      vin: truck?.vin,
      mileage: truck?.mileage,
      hours: truck?.hours,
      reason: truck?.reason,
      warranty: !!truck?.warranty,
      isHardDown: !!truck?.isHardDown,
      techNumbers: getTechSummary(truck),
      repairCategories: truck?.repairCategories || [],
      statusHistory: truck?.statusHistory || [],
      notes,
    },
  };
}

export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
